// Full-screen overlay shown when the microphone permission has been denied
// at the OS or WebView level and the user tries to unmute (or Jitsi
// otherwise fails to acquire an audio track).
//
// On Android Chromium WebView the deny is cached at the origin level, so
// no amount of retrying getUserMedia will re-trigger the OS popup — the
// only way to recover is for the user to enable microphone access via
// Phone Settings. Because a plain web page cannot open the native
// Settings app, we postMessage `OPEN_SETTINGS` to the enclosing React
// Native WebView and let the wrapper call `Linking.openSettings()`.
//
// z-index 10001 sits above CustomWaitingRoom (10000) and the Jitsi
// iframe (9999) so the modal is always the top-most surface.
const PermissionErrorModal = ({ visible, onOpenSettings, onClose }) => {
    if (!visible) {
        return null;
    }

    return (
        <div style={styles.backdrop}>
            <div style={styles.card}>
                <div style={styles.iconWrap}>
                    <div style={styles.iconCircle}>
                        {/* Simple mic-off glyph — inline SVG to avoid asset dependency */}
                        <svg width='36' height='36' viewBox='0 0 24 24' fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path d='M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z'
                                stroke='#fff' strokeWidth='2' strokeLinecap='round'
                                strokeLinejoin='round' />
                            <path d='M19 12a7 7 0 0 1-14 0M12 19v3M4 4l16 16'
                                stroke='#fff' strokeWidth='2' strokeLinecap='round'
                                strokeLinejoin='round' />
                        </svg>
                    </div>
                </div>

                <h2 style={styles.title}>Microphone access needed</h2>

                <p style={styles.body}>
                    Your microphone is blocked. To unmute yourself, please enable
                    microphone access for this app.
                </p>

                <ol style={styles.steps}>
                    <li>Tap <strong>Open Settings</strong> below</li>
                    <li>Turn on <strong>Microphone</strong></li>
                    <li>Come back and try unmute again</li>
                </ol>

                <div style={styles.buttonRow}>
                    <button
                        type='button'
                        style={styles.secondaryBtn}
                        onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type='button'
                        style={styles.primaryBtn}
                        onClick={onOpenSettings}>
                        Open Settings
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 24, 40, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
        background: '#fff',
        borderRadius: '16px',
        padding: '28px 24px 20px',
        maxWidth: '360px',
        width: '100%',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
        textAlign: 'center',
        boxSizing: 'border-box'
    },
    iconWrap: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '16px'
    },
    iconCircle: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: '#df486f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    title: {
        margin: '0 0 12px',
        fontSize: '20px',
        fontWeight: 600,
        color: '#1a1a1a'
    },
    body: {
        margin: '0 0 16px',
        fontSize: '14px',
        color: '#4a4a4a',
        lineHeight: 1.5
    },
    steps: {
        margin: '0 0 20px',
        paddingLeft: '24px',
        fontSize: '14px',
        color: '#4a4a4a',
        lineHeight: 1.8,
        textAlign: 'left'
    },
    buttonRow: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'stretch'
    },
    secondaryBtn: {
        flex: 1,
        padding: '12px 16px',
        borderRadius: '10px',
        border: '1px solid #d0d3dc',
        background: '#fff',
        color: '#1a1a1a',
        fontSize: '15px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    primaryBtn: {
        flex: 1,
        padding: '12px 16px',
        borderRadius: '10px',
        border: 'none',
        background: '#0056e0',
        color: '#fff',
        fontSize: '15px',
        fontWeight: 600,
        cursor: 'pointer'
    }
};

export default PermissionErrorModal;
